import React, { useState, useCallback } from 'react';
import {
  Box, Grid, Select, MenuItem, FormControl, InputLabel,
  TextField, Chip, Stack, Switch, FormControlLabel, useTheme,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  People,
  MouseRounded,
  TrendingUp,
  SwapHoriz,
  AccessTime,
  PersonSearch,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { SectionHeader, GlassTabs } from '../../../components/admin/ui';
import AnalyticsOverview from './AnalyticsOverview';
import AnalyticsAudience from './AnalyticsAudience';
import AnalyticsBehavior from './AnalyticsBehavior';
import AnalyticsAcquisition from './AnalyticsAcquisition';
import AnalyticsConversions from './AnalyticsConversions';
import AnalyticsRealtime from './AnalyticsRealtime';
import AnalyticsUsers from './AnalyticsUsers';

// ─── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { value: 'overview', label: 'Overview', icon: <BarChartIcon sx={{ fontSize: 16 }} /> },
  { value: 'audience', label: 'Audience', icon: <People sx={{ fontSize: 16 }} /> },
  { value: 'behavior', label: 'Behavior', icon: <MouseRounded sx={{ fontSize: 16 }} /> },
  { value: 'acquisition', label: 'Acquisition', icon: <TrendingUp sx={{ fontSize: 16 }} /> },
  { value: 'conversions', label: 'Conversions', icon: <SwapHoriz sx={{ fontSize: 16 }} /> },
  { value: 'realtime', label: 'Realtime', icon: <AccessTime sx={{ fontSize: 16 }} /> },
  { value: 'users', label: 'Users', icon: <PersonSearch sx={{ fontSize: 16 }} /> },
];

// ─── Date preset helpers ──────────────────────────────────────────────────────

function toISODate(date) {
  return date.toISOString().slice(0, 10);
}

function getPresetRange(preset) {
  const today = new Date();
  const fmt = toISODate;
  switch (preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today) };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: fmt(y), to: fmt(y) };
    }
    case '7d': {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: fmt(d), to: fmt(today) };
    }
    case '30d': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: fmt(d), to: fmt(today) };
    }
    case '90d': {
      const d = new Date(today);
      d.setDate(d.getDate() - 89);
      return { from: fmt(d), to: fmt(today) };
    }
    default:
      return null;
  }
}

const DEFAULT_PRESET = '7d';

// ─── Role options ─────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'student', label: 'Student' },
  { value: 'librarian', label: 'Librarian' },
  { value: 'admin', label: 'Admin' },
  { value: 'institute_manager', label: 'Institute Manager' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'seo', label: 'SEO' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state — synced with URL
  const activeTab = searchParams.get('tab') || 'overview';

  // Date range state
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const initRange = getPresetRange(DEFAULT_PRESET);
  const [from, setFrom] = useState(initRange.from);
  const [to, setTo] = useState(initRange.to);

  // Filter state
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [country] = useState('');
  const [device, setDevice] = useState('');
  const [newReturning, setNewReturning] = useState('all');
  const [includeBots, setIncludeBots] = useState(false);
  const [includeInternalRoles, setIncludeInternalRoles] = useState(false);

  const handleTabChange = useCallback((_, newVal) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', newVal);
      return next;
    });
  }, [setSearchParams]);

  const handlePresetChange = useCallback((e) => {
    const val = e.target.value;
    setPreset(val);
    if (val !== 'custom') {
      const range = getPresetRange(val);
      if (range) { setFrom(range.from); setTo(range.to); }
    }
  }, []);

  // Shared filter object passed down to each tab
  const filters = {
    from,
    to,
    roles: selectedRoles,
    country: country || undefined,
    device: device || undefined,
    newReturning,
    includeBots,
    includeInternalRoles,
  };

  return (
    <Box>
      <SectionHeader
        eyebrow="Admin"
        title="Analytics"
        description="Track visitor behavior, conversions, and platform health across all roles."
      />

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          background: isDark ? 'rgba(15,18,32,0.45)' : 'rgba(255,255,255,0.45)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)'}`,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Date preset */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Range</InputLabel>
              <Select value={preset} onChange={handlePresetChange} label="Range">
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Custom date inputs */}
          {preset === 'custom' && (
            <>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField
                  label="From"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: to }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField
                  label="To"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: from }}
                />
              </Grid>
            </>
          )}

          {/* Device filter */}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Device</InputLabel>
              <Select
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                label="Device"
              >
                <MenuItem value="">All Devices</MenuItem>
                <MenuItem value="desktop">Desktop</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
                <MenuItem value="tablet">Tablet</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* New/Returning */}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Visitors</InputLabel>
              <Select
                value={newReturning}
                onChange={(e) => setNewReturning(e.target.value)}
                label="Visitors"
              >
                <MenuItem value="all">All Visitors</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="returning">Returning</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Role chips */}
          <Grid item xs={12} md>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
              {ROLE_OPTIONS.map((r) => (
                <Chip
                  key={r.value}
                  label={r.label}
                  size="small"
                  variant={selectedRoles.includes(r.value) ? 'filled' : 'outlined'}
                  onClick={() =>
                    setSelectedRoles((prev) =>
                      prev.includes(r.value)
                        ? prev.filter((v) => v !== r.value)
                        : [...prev, r.value]
                    )
                  }
                  sx={{
                    cursor: 'pointer',
                    ...(selectedRoles.includes(r.value)
                      ? { background: 'rgba(99,102,241,0.85)', color: '#fff', border: 'none' }
                      : {}),
                  }}
                />
              ))}
            </Stack>
          </Grid>

          {/* Toggles */}
          <Grid item xs={12} md="auto">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeInternalRoles}
                    onChange={(e) => setIncludeInternalRoles(e.target.checked)}
                  />
                }
                label={
                  <Box component="span" sx={{ fontSize: '0.78rem' }}>
                    Internal roles
                  </Box>
                }
                sx={{ mr: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeBots}
                    onChange={(e) => setIncludeBots(e.target.checked)}
                  />
                }
                label={
                  <Box component="span" sx={{ fontSize: '0.78rem' }}>
                    Bots
                  </Box>
                }
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <GlassTabs
        value={activeTab}
        onChange={handleTabChange}
        tabs={TABS}
        layoutId="analytics-tabs"
      />

      {/* ── Tab panels ───────────────────────────────────────────────────── */}
      {activeTab === 'overview' && <AnalyticsOverview filters={filters} />}
      {activeTab === 'audience' && <AnalyticsAudience filters={filters} />}
      {activeTab === 'behavior' && <AnalyticsBehavior filters={filters} />}
      {activeTab === 'acquisition' && <AnalyticsAcquisition filters={filters} />}
      {activeTab === 'conversions' && <AnalyticsConversions filters={filters} />}
      {activeTab === 'realtime' && <AnalyticsRealtime />}
      {activeTab === 'users' && <AnalyticsUsers filters={filters} />}
    </Box>
  );
}
